import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Link2, Plus, Trash2, ExternalLink, FolderOpen } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useCollection } from "@/hooks/useCollection";

const COLORS = ["#8FA9E3", "#6FC2B0", "#EE9C86", "#B69CE0", "#78B7E0", "#E3B7D9"];
const SUGGESTED = ["District Tools", "Curriculum", "LMS", "Forms", "Slides", "Websites", "General"];

export default function Resources() {
  const { items, loading, create, remove } = useCollection("resources");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", category: "General", color: COLORS[0] });

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => {
      const c = i.category || "General";
      (g[c] = g[c] || []).push(i);
    });
    return g;
  }, [items]);

  const save = async () => {
    if (!form.title.trim() || !form.url.trim()) return toast.error("Add a name and link");
    await create(form);
    toast.success("Link saved to your toolbox");
    setForm({ title: "", url: "", category: form.category, color: COLORS[Math.floor(Math.random() * COLORS.length)] });
    setOpen(false);
  };

  return (
    <div>
      <PageHeader
        title="Resource Hub" icon={Link2}
        subtitle="Your teacher toolbox — every tool, form and site, one click away."
        action={<Button onClick={() => setOpen(true)} data-testid="add-resource-button" className="rounded-full bg-primary hover:bg-primary/90"><Plus size={18} className="mr-1" /> Add link</Button>}
      />

      {loading ? <p className="text-slate-400">Loading…</p> : items.length === 0 ? (
        <div className="tcc-card p-12 text-center">
          <FolderOpen className="mx-auto text-primary/50 mb-3" size={40} strokeWidth={1.4} />
          <p className="font-heading font-bold text-slate-700 text-lg">Build your toolbox</p>
          <p className="text-slate-400 text-sm mt-1 mb-5">Add your district portal, LMS, gradebook, slides, and favorite sites.</p>
          <Button onClick={() => setOpen(true)} className="rounded-full bg-primary hover:bg-primary/90"><Plus size={18} className="mr-1" /> Add your first link</Button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, links]) => (
            <div key={cat} data-testid="resource-category">
              <h2 className="font-heading font-bold text-slate-700 mb-3 flex items-center gap-2"><FolderOpen size={17} className="text-primary" /> {cat} <span className="text-xs text-slate-400 font-normal">({links.length})</span></h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {links.map((l) => (
                  <div key={l.id} className="tcc-card p-4 flex items-center gap-3 group hover:-translate-y-0.5 transition-transform duration-200" data-testid="resource-item">
                    <span className="h-11 w-11 rounded-2xl flex items-center justify-center text-white font-bold shrink-0" style={{ background: l.color }}>{l.title[0]?.toUpperCase()}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{l.title}</p>
                      <p className="text-xs text-slate-400 truncate">{l.url}</p>
                    </div>
                    <a href={l.url.startsWith("http") ? l.url : `https://${l.url}`} target="_blank" rel="noreferrer" className="p-2 rounded-xl text-slate-400 hover:text-primary hover:bg-primary/10" data-testid="resource-open"><ExternalLink size={16} /></a>
                    <button onClick={() => remove(l.id)} className="p-2 rounded-xl text-slate-300 hover:text-destructive opacity-0 group-hover:opacity-100 transition"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl" data-testid="resource-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add a link</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title-input" className="rounded-xl mt-1" placeholder="Google Classroom" /></div>
            <div><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="resource-url-input" className="rounded-xl mt-1" placeholder="https://classroom.google.com" /></div>
            <div>
              <Label>Category</Label>
              <Input list="cats" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="resource-category-input" className="rounded-xl mt-1" placeholder="Group…" />
              <datalist id="cats">{SUGGESTED.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-1.5">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })} className={`h-8 w-8 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-slate-800 scale-110" : ""}`} style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="resource-save-button" className="rounded-full bg-primary hover:bg-primary/90">Save link</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
